/* eslint-disable @typescript-eslint/no-shadow */

import React, { useEffect, useState } from 'react';
import type { Checklist } from '../../../containers/Checklists/checklistsSlice/types';
import type { GroupDetail } from '../../../containers/GroupDetail/groupDetailSlice/types';
import { useTenantId } from '../../../utils/customHooks/useTenantId';
import { selectTenant } from '../../../containers/Organizations/OrganizationsSlice';
import { useAppSelector } from '../../../hooks';
import { useLayoutContext } from '../../../utils/customHooks/LayoutContext';
import { Backdrop } from '../../Backdrop/Backdrop';
import { SCancel } from '../../Options/Options.styles';
import { selectUserPermissions } from '../../../containers/Login/LoginSlice';
import { useParams } from 'react-router-dom';
import ChecklistDetailsButtons from './ChecklistDetailsButtons';
import { translate } from '../../../utils/translate';
import ChecklistInfo from './ChecklistInfo';
import { SChecklistSettingsContainer } from './ChecklistDetails.styles';
import { getGroupById } from '../../../api/groups';

type ChecklistDetailsProps = {
  isOpen: boolean;
  setIsOpen: () => void;
  data: Checklist | null;
  setAddItemModalOpen?: () => void;
};

function ChecklistDetails(props: ChecklistDetailsProps) {
  const { isOpen, setIsOpen, data, setAddItemModalOpen } = props;

  const { id: idFromUrl } = useParams();
  const layout = useLayoutContext();
  const tenantId = useTenantId();

  const userPermissions = useAppSelector(selectUserPermissions);
  const tenant = useAppSelector(selectTenant);
  const insideChecklist = idFromUrl !== undefined;

  const [foundSharedGroups, setFoundSharedGroups] = useState<GroupDetail[]>([]);

  const getGroups = async () => {
    if (!data?.sharedGroups || !tenantId) {
      return;
    }
    const tempArr: GroupDetail[] = [];
    // eslint-disable-next-line no-unsafe-optional-chaining
    for await (const groupId of data?.sharedGroups) {
      try {
        if (!tempArr.find(group => group.id === groupId)) {
          const result = await getGroupById({ groupId, tenantId });

          if (result.error) {
            throw new Error();
          }

          tempArr.push(result.data.data);
        }
      } catch (error) {
        console.error('Error: Failed to find group');
      }
    }
    return tempArr;
  };

  useEffect(() => {
    let isMounted = true;
    getGroups().then(data => {
      if (isMounted && data) setFoundSharedGroups(data);
    });
    return () => {
      isMounted = false;
    };
  }, [data, isOpen, tenantId]);

  useEffect(() => {
    layout.setTabsState(!isOpen);
    if (!isOpen) setFoundSharedGroups([]);
  }, [isOpen]);

  if (!data) return <></>;

  const { id, name, status } = data;

  if (!isOpen) return <></>;

  return (
    <>
      <SChecklistSettingsContainer role="checklistSettingsContainer">
        <ChecklistInfo data={data} foundSharedGroups={foundSharedGroups} />
        <ChecklistDetailsButtons
          status={status}
          insideChecklist={insideChecklist}
          id={id}
          setIsOpen={setIsOpen}
          name={name}
          setAddItemModalOpen={setAddItemModalOpen}
          checklistPermissions={userPermissions?.checklists}
        />
        <SCancel onClick={setIsOpen} className="wrapper cancel" role="cancelButton">
          <p role="cancelText">{translate(`cancel`)}</p>
        </SCancel>
      </SChecklistSettingsContainer>
      <Backdrop setModal={setIsOpen} />
    </>
  );
}

export default ChecklistDetails;
